/**
 * Cookie consent + Google Consent Mode v2.
 *
 * Contract:
 *   - Nothing advertising-related loads until the visitor chooses. The denied
 *     defaults are set in index.html before any tag runs.
 *   - "Accept All"     -> consent granted, AdSense loaded, personalised ads.
 *   - "Essential Only" -> consent denied, AdSense NOT loaded.
 *   - The choice is stored and re-applied silently on later visits.
 *
 * On "Essential Only" we deliberately load no ad script at all. Non-personalised
 * ads still read and write cookies, so serving them to someone who declined
 * would contradict both the button label and the denied Consent Mode signal.
 * To serve NPA ads on decline instead, set ADS_ON_DECLINE to true — and take
 * advice first, because under ePrivacy that generally still requires consent.
 */
const ADS_ON_DECLINE = false;

export const CONSENT_STORAGE_KEY = 'npw_cookie_consent';
// AdSense publisher supplied by the site owner; keep the verification meta tag in index.html in sync.
export const ADSENSE_CLIENT = 'ca-pub-5539837499737231';
const ADSENSE_SRC = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;

export type ConsentChoice = 'granted' | 'denied';

type GtagFn = (...args: unknown[]) => void;
interface ConsentWindow extends Window {
  gtag?: GtagFn;
  dataLayer?: unknown[];
  adsbygoogle?: unknown[] & { requestNonPersonalizedAds?: number };
}

function win(): ConsentWindow | null {
  return typeof window === 'undefined' ? null : (window as ConsentWindow);
}

/** Read the stored choice. Returns null when the visitor has not decided yet. */
export function readConsent(): ConsentChoice | null {
  try {
    const v = localStorage.getItem(CONSENT_STORAGE_KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null; // storage blocked — treat as undecided
  }
}

function writeConsent(choice: ConsentChoice): void {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, choice);
  } catch { /* storage blocked; the choice applies for this page view only */ }
}

/** Push a Consent Mode v2 update. Falls back to dataLayer if gtag is absent. */
function updateConsentMode(choice: ConsentChoice): void {
  const w = win();
  if (!w) return;
  const value = choice === 'granted' ? 'granted' : 'denied';
  const payload = {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  };
  if (typeof w.gtag === 'function') {
    w.gtag('consent', 'update', payload);
  } else {
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push(['consent', 'update', payload]);
  }
}

/** Inject the AdSense tag once. Safe to call repeatedly. */
function loadAdSense(personalized: boolean): void {
  const w = win();
  if (!w || typeof document === 'undefined') return;
  if (document.querySelector(`script[src^="${ADSENSE_SRC}"]`)) return;

  if (!personalized) {
    // AdSense reads this flag off the adsbygoogle queue. (googletag /
    // setRequestNonPersonalizedAds is the Ad Manager API, not AdSense.)
    w.adsbygoogle = w.adsbygoogle || [];
    w.adsbygoogle.requestNonPersonalizedAds = 1;
  }

  const s = document.createElement('script');
  s.async = true;
  s.src = ADSENSE_SRC;
  s.crossOrigin = 'anonymous';
  document.head.appendChild(s);
}

/** Listeners notified whenever the choice is applied, so ad slots can react. */
const listeners = new Set<(choice: ConsentChoice) => void>();

/** Subscribe to consent changes. Returns an unsubscribe function. */
export function onConsentChange(cb: (choice: ConsentChoice) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * Apply a choice: update Consent Mode, load (or withhold) advertising, persist.
 * @param persist false when replaying a stored choice on a later visit.
 */
export function applyConsent(choice: ConsentChoice, persist = true): void {
  if (persist) writeConsent(choice);
  updateConsentMode(choice);

  if (choice === 'granted') {
    loadAdSense(true);
  } else if (ADS_ON_DECLINE) {
    loadAdSense(false);
  }
  // choice === 'denied' with ADS_ON_DECLINE false: no ad script is loaded.

  listeners.forEach(cb => cb(choice));
}

/** True when advertising is permitted to render right now. */
export function adsAllowed(): boolean {
  return readConsent() === 'granted' || (readConsent() === 'denied' && ADS_ON_DECLINE);
}

/**
 * Re-apply a stored choice on load. Returns true when a choice exists, so the
 * banner knows to stay hidden.
 */
export function initStoredConsent(): boolean {
  const stored = readConsent();
  if (!stored) return false;
  applyConsent(stored, false);
  return true;
}

/** Clear the stored choice so the banner shows again. */
export function resetConsent(): void {
  try { localStorage.removeItem(CONSENT_STORAGE_KEY); } catch { /* ignore */ }
}

/** Listeners that re-open the banner when the visitor asks to change settings. */
const repromptListeners = new Set<() => void>();

export function onConsentReprompt(cb: () => void): () => void {
  repromptListeners.add(cb);
  return () => repromptListeners.delete(cb);
}

/**
 * Re-open the consent banner from a "Cookie settings" control.
 *
 * Withdrawal must be as easy as granting, so this also signals denied straight
 * away rather than waiting for a new choice — any further ad requests respect it
 * immediately. A script already on the page cannot be unloaded, so anything
 * AdSense loaded earlier in this page view persists until the next reload.
 */
export function openConsentSettings(): void {
  resetConsent();
  updateConsentMode('denied');
  repromptListeners.forEach(cb => cb());
}
